import * as React from 'react';
import {
  Admin,
  AuthProvider,
  CustomRoutes,
  Resource,
  useTranslate,
} from 'react-admin';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import { Route } from 'react-router';
import { Login, Layout } from './layout';
import englishMessages from './i18n/en';
import { darkTheme, lightTheme } from './layout/themes';
import authProvider from './dataProviders/authProvider';
import dataProvider from './dataProviders/dataProvider';
import Dashboard from './dashboard/Dashboard';

import CategoryList from './pages/category/List';

import MembershipList from './pages/membership/List';
import MembershipCreate from './pages/membership/Create';
import MembershipEdit from './pages/membership/Edit';

import TaxTypeList from './pages/taxtype/List';
import TaxTypeCreate from './pages/taxtype/Create';
import TaxTypeEdit from './pages/taxtype/Edit';

import UserList from './pages/user/List';
import UserCreate from './pages/user/Create';
import UserEdit from './pages/user/Edit';

import AddressList from './pages/address/List';
import AddressCreate from './pages/address/Create';
import AddressEdit from './pages/address/Edit';

import RoleList from './pages/role/List';
import RoleCreate from './pages/role/Create';
import RoleEdit from './pages/role/Edit';

import ProductList from './pages/product/List';

import CouponList from './pages/coupon/List';
import CouponCreate from './pages/coupon/Create';
import CouponEdit from './pages/coupon/Edit';

import AttributeList from './pages/attribute/List';
import AttributeCreate from './pages/attribute/Create';
import AttributeEdit from './pages/attribute/Edit';

import ProductAttributeList from './pages/productattribute/List';
import ProductAttributeCreate from './pages/productattribute/Create';
import ProductAttributeEdit from './pages/productattribute/Edit';

import DealList from './pages/deal/List';
import DealCreate from './pages/deal/Create';
import DealEdit from './pages/deal/Edit';

import OrderList from './pages/order/List';
import OrderCreate from './pages/order/Create';
import OrderEdit from './pages/order/Edit';

import CartList from './pages/cart/List';
import CartCreate from './pages/cart/Create';
import CartEdit from './pages/cart/Edit';

import ReviewsList from './pages/reviews/List';
import ReviewsCreate from './pages/reviews/Create';
import ReviewsEdit from './pages/reviews/Edit';

import LanguageList from './pages/language/List';
import LanguageCreate from './pages/language/Create';
import LanguageEdit from './pages/language/Edit';

import TranslationList from './pages/translation/List';
import TranslationCreate from './pages/translation/Create';
import TranslationEdit from './pages/translation/Edit';

import CountryList from './pages/country/List';
import CountryCreate from './pages/country/Create';
import CountryEdit from './pages/country/Edit';

import ShipmentPriceList from './pages/shipmentPrice/List';
import ShipmentPriceCreate from './pages/shipmentPrice/Create';
import ShipmentPriceEdit from './pages/shipmentPrice/Edit';

import ProductDetails from './pages/product/productDetails/ProductDetails';
import CategoryChange from './pages/category/CategoryChange';
import {
  CardMembership,
  Class,
  Discount,
  Group,
  Inventory,
  Lock,
  PointOfSale,
  RememberMe,
} from '@mui/icons-material';
import SettingPage from './pages/setting/SettingPage';

const i18nProvider = polyglotI18nProvider((locale) => {
  if (locale === 'sv') {
    return import('./i18n/sv').then((messages) => messages.default);
  }

  if (locale === 'da') {
    return import('./i18n/da').then((messages) => messages.default);
  }

  // Always fallback on english
  return englishMessages;
}, 'en');

const App = () => {
  const t = useTranslate();

  return (
    <Admin
      title=''
      dataProvider={dataProvider}
      authProvider={authProvider}
      dashboard={Dashboard}
      loginPage={Login}
      layout={Layout}
      i18nProvider={i18nProvider}
      disableTelemetry
      theme={lightTheme}
    >
      <Resource
        name={'category'}
        list={CategoryList}
        create={CategoryChange}
        edit={CategoryChange}
        icon={() => <Class />}
      />
      <Resource
        name={'product'}
        list={ProductList}
        create={ProductDetails}
        edit={ProductDetails}
        icon={() => <Inventory />}
      />
      <Resource
        name={'order'}
        list={OrderList}
        create={OrderCreate}
        edit={OrderEdit}
      />
      <Resource
        name={'membership'}
        list={MembershipList}
        create={MembershipCreate}
        edit={MembershipEdit}
        icon={() => <CardMembership />}
      />
      <Resource
        name={'taxtype'}
        list={TaxTypeList}
        create={TaxTypeCreate}
        edit={TaxTypeEdit}
        icon={() => <PointOfSale />}
      />
      <Resource
        name={'user'}
        list={UserList}
        create={UserCreate}
        edit={UserEdit}
        icon={() => <Group />}
      />
      <Resource
        name={'role'}
        list={RoleList}
        create={RoleCreate}
        edit={RoleEdit}
        icon={() => <Lock />}
      />

      <Resource
        name={'coupon'}
        list={CouponList}
        create={CouponCreate}
        edit={CouponEdit}
        icon={() => <Discount />}
      />
      <Resource
        name={'attribute'}
        list={AttributeList}
        create={AttributeCreate}
        edit={AttributeEdit}
      />
      <Resource
        name={'deal'}
        list={DealList}
        create={DealCreate}
        edit={DealEdit}
      />

      <Resource
        name={'cart'}
        list={CartList}
        create={CartCreate}
        edit={CartEdit}
      />
      <Resource
        name={'reviews'}
        list={ReviewsList}
        create={ReviewsCreate}
        edit={ReviewsEdit}
      />
      <Resource
        name={'language'}
        list={LanguageList}
        create={LanguageCreate}
        edit={LanguageEdit}
      />
      <Resource
        name={'translation'}
        list={TranslationList}
        create={TranslationCreate}
        edit={TranslationEdit}
      />
      <Resource
        name={'country'}
        list={CountryList}
        create={CountryCreate}
        edit={CountryEdit}
      />

      <Resource
        name={'shipmentPrice'}
        list={ShipmentPriceList}
        create={ShipmentPriceCreate}
        edit={ShipmentPriceEdit}
      />
      <Resource name={'setting'} list={SettingPage} />
    </Admin>
  );
};

export default App;
